import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Clock, Home, LogOut, Mail, XCircle, Loader } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, errorMessage } from '../lib/api';
import { useToast } from '../components/ui/Toast';
import { COMMITTEE_EMAIL } from '../lib/committee';
import { MovedOut } from './MovedOut';
import type { HouseholdClaim } from '../types';
import './PendingApproval.css';

/** What `GET /api/auth/pending` tells an account that is not approved yet. */
interface PendingStatus {
    account_status: string;
    email_verified: boolean;
    email: string;
    /** The address they picked on sign-up, formatted. Absent for free-text sign-ups. */
    address_label?: string | null;
    /** What they said about the household when they signed up. */
    household_claim?: HouseholdClaim | null;
    /** The replacement or join request they made, when there is one. */
    address_request?: {
        kind: 'replace' | 'join';
        status: 'pending' | 'approved' | 'declined';
        address_label?: string | null;
    } | null;
}

export function PendingApproval() {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const [signingOut, setSigningOut] = useState(false);
    const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
    const [status, setStatus] = useState<PendingStatus | null>(null);

    // A pending account is not given its full profile row, so the status comes
    // from the auth endpoint instead.
    useEffect(() => {
        let cancelled = false;
        apiGet<PendingStatus>('/api/auth/pending')
            .then((s) => { if (!cancelled) setStatus(s); })
            .catch((err) => console.warn('Could not read the account status', err));
        return () => { cancelled = true; };
    }, []);

    const email = status?.email ?? user?.email ?? '';
    const accountStatus = status?.account_status ?? user?.account_status;
    const rejected = accountStatus === 'rejected';
    const emailUnconfirmed = !(status?.email_verified ?? user?.email_verified ?? true);

    // The claim can arrive either as the sign-up answer or as the request row
    // it created; both say the same thing to the resident.
    const request = status?.address_request ?? null;
    const claim: HouseholdClaim | null = status?.household_claim
        ?? (request ? (request.kind === 'replace' ? 'replace' : 'family') : null);
    const addressLine = request?.address_label ?? status?.address_label ?? null;

    /** One sentence about the household, or null when there is nothing to say. */
    const householdMessage = (): string | null => {
        if (!claim || !addressLine) return null;
        if (claim === 'replace') {
            if (request?.status === 'declined') {
                return `The committee couldn't confirm your move to ${addressLine}. Email them if you think that's wrong.`;
            }
            return `Your request to take over ${addressLine} is waiting for the committee.`;
        }
        if (claim === 'family') return `You asked to join the household at ${addressLine}.`;
        return `You registered ${addressLine}.`;
    };
    const household = householdMessage();

    const handleSignOut = async () => {
        setSigningOut(true);
        try {
            await signOut();
            navigate('/', { replace: true });
        } finally {
            setSigningOut(false);
        }
    };

    const resendConfirmation = async () => {
        if (!email || resendState !== 'idle') return;
        setResendState('sending');
        try {
            await apiPost('/api/auth/resend-verification', { email });
        } catch (err) {
            toast.error(errorMessage(err, "Couldn't resend right now. Please try again in a minute."));
            setResendState('idle');
            return;
        }
        toast.success('Confirmation email sent.');
        setResendState('sent');
        window.setTimeout(() => setResendState('idle'), 60_000);
    };

    // A replaced resident keeps their data but not the resident pages.
    if (accountStatus === 'moved_out') return <MovedOut />;

    return (
        <div className="pending-page">
            <div className="pending-container animate-fade-in">
                <div className={`pending-icon ${rejected ? 'pending-icon-rejected' : ''}`}>
                    {rejected ? <XCircle size={48} /> : <Clock size={48} />}
                </div>

                <h1>{rejected ? 'Application not approved' : 'Application pending'}</h1>

                {rejected ? (
                    <p className="pending-message">
                        Your application wasn't approved. If you think this is a mistake, email the committee at{' '}
                        <a href={`mailto:${COMMITTEE_EMAIL}`}>{COMMITTEE_EMAIL}</a>.
                    </p>
                ) : (
                    <p className="pending-message">
                        Thanks for registering! The committee is checking that you live in Forest Town before switching on your account.
                    </p>
                )}

                {email && (
                    <p className="pending-email">
                        <Mail size={16} aria-hidden="true" />
                        Signed in as <strong>{email}</strong>
                    </p>
                )}

                {household && (
                    <p className="pending-message" data-testid="household-claim">{household}</p>
                )}

                {!rejected && (
                    <div className="pending-info card">
                        <div className="info-row">
                            <Home size={20} aria-hidden="true" />
                            <div>
                                <strong>What happens next?</strong>
                                <p>An admin will verify your Forest Town address and approve your account. This usually takes 1–2 days.</p>
                            </div>
                        </div>
                    </div>
                )}

                {emailUnconfirmed && (
                    <div className="inline-alert inline-alert-warning pending-alert">
                        <div>
                            <strong>Please confirm your email.</strong> We sent a link to your inbox — we can't approve you until it's confirmed.
                            <div className="pending-alert-action">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={resendConfirmation}
                                    disabled={resendState !== 'idle'}
                                >
                                    {resendState === 'sending' && <Loader className="spinner-icon" size={14} />}
                                    {resendState === 'sent' ? 'Sent' : resendState === 'sending' ? 'Sending…' : 'Resend confirmation email'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {!rejected && <p className="pending-note">You'll get an email when you're approved.</p>}

                <button type="button" className="btn btn-secondary" onClick={handleSignOut} disabled={signingOut}>
                    {signingOut ? <Loader className="spinner-icon" size={18} /> : <LogOut size={18} />}
                    Sign out
                </button>

                <p className="pending-privacy">
                    <Link to="/privacy">Privacy Notice</Link>
                </p>
            </div>
        </div>
    );
}
